/**
 * `<DeepLinkDispatcher />` — router-internal subscriber that re-runs the
 * `applyDeepLink` dispatcher on every `location.key` change.
 *
 * Mounted inside the data-router `<Root>` element so `useLocation()`
 * resolves; the component itself renders nothing. The actual side-effect
 * logic lives in `./deepLink.ts` so it can be unit-tested without
 * mounting React.
 *
 * Idempotency: a `lastKeyRef` guards against double dispatch when React
 * re-runs the effect under StrictMode. This still satisfies dispatch §4
 * step 4's "calling it twice with the same hash MUST be a no-op for
 * state mutations" because the upstream pure helper is also a pure
 * function of `(hash, search)`.
 *
 * Cite:
 *   - dispatch `WP-E1-routes-foundation-1.md` §4 step 4 + AC5.
 *   - `architecture.md` §3.3.
 */

import { useEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useViewerStore } from '@/state/useViewerStore';
import { useDrawerHost } from '@/state/drawerHost';
import { useLiveRegion } from '@/state/liveRegion';
import { applyDeepLink, type DeepLinkDeps } from './deepLink';

export function DeepLinkDispatcher() {
  const location = useLocation();
  const liveRegion = useLiveRegion();
  const lastKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (lastKeyRef.current === location.key) return;
    lastKeyRef.current = location.key;
    const search = new URLSearchParams(location.search);
    const deps: DeepLinkDeps = {
      hydrateFromUrlState: (payload, opts) =>
        useViewerStore.getState().hydrateFromUrlState(payload, opts),
      setMode: (mode) => useViewerStore.getState().setMode(mode),
      drawerHostOpen: (id, onClose) => useDrawerHost.getState().open(id, onClose),
      liveRegionAnnounce: (role, text) => liveRegion.announce(role, text),
      setActivePreset: (presetId) => useViewerStore.getState().setActivePreset(presetId),
    };
    applyDeepLink(location.hash, search, deps);
  }, [liveRegion, location.hash, location.key, location.search]);

  return null;
}

/**
 * `<Root />` — the route-level shell that mounts the dispatcher and
 * renders the matching child route via `<Outlet />`. Used as the root
 * element of every top-level route so `useLocation()` always resolves
 * inside a `<RouterProvider />` context.
 */
export function Root() {
  return (
    <>
      <DeepLinkDispatcher />
      <Outlet />
    </>
  );
}
